import React from "react";
import Skeleton from "react-loading-skeleton";
import PermissionsGate from "../../../../utils/permissionGate";
import IconButton, { StyledActionGroup, StyledIconLink } from "../../../library/IconButton";
import { StyledCampusBadge } from "./linkedProgrammes.styles";
import {
    StyledCell,
    StyledCheckboxCell,
    StyledGradeAction,
    StyledGradeBadge,
    StyledGradeCellContent,
    StyledGradeMeta,
    StyledGradeStatus,
    StyledMethod,
    StyledModulesButton,
    StyledProgrammeCell,
    StyledProgrammeDetails,
    StyledProgrammeMetaRow,
    StyledProgrammeName,
    StyledProgrammeNameLink,
    StyledQaGradesCell,
    StyledQaIntakeCell,
    StyledQaProgrammeCell,
    StyledQaStudentsCell,
    StyledQaActionsCell,
    StyledQaVersionCell,
    StyledReadinessCell,
    StyledReadinessCount,
    StyledReadinessRow,
    StyledReadinessState,
    StyledReadinessStateIcon,
    StyledReadinessText,
    StyledReadinessWarning,
    StyledReadinessWarnings,
    StyledStudentCell,
    StyledVersionActiveIcon,
    StyledVersionInactiveIcon,
    StyledVersionValue,
} from "./LinkedProgrammesTable.styles";
import {
    getCampusText,
    getGradeMeta,
    getMethodIcon,
    getMethodText,
    getReadiness,
    isVersionActive,
} from "../../../../utils/LinkedProgrammeUtils";

/**
 * "Programme Details" cell: name (linked if it has an id), campus and delivery method.
 * @param {object} row Linked-programme row.
 */
export function ProgrammeDetailsCell({ row }) {
    const campusText = getCampusText(row?.campus);
    const methodText = getMethodText(row?.type);

    return (
        <StyledProgrammeDetails>
            {row?.id ? (
                <StyledProgrammeNameLink
                    to={`/courseAdministration/Programmes/programme/open/${row.id}/details`}
                    title={row?.name}
                >
                    {row?.name || "-"}
                </StyledProgrammeNameLink>
            ) : (
                <StyledProgrammeName title={row?.name}>
                    {row?.name || "-"}
                </StyledProgrammeName>
            )}

            <StyledProgrammeMetaRow>
                <StyledCampusBadge $campus={campusText}>
                    {campusText}
                </StyledCampusBadge>

                <StyledMethod>
                    <i
                        className={`fal fa-${getMethodIcon(methodText)}`}
                        aria-hidden="true"
                    />
                    {methodText}
                </StyledMethod>
            </StyledProgrammeMetaRow>
        </StyledProgrammeDetails>
    );
}

/**
 * "Version" cell: active/inactive icon plus the version name.
 * @param {object} row Linked-programme row.
 */
export function VersionCell({ row }) {
    if (!row?.version_name) {
        return "-";
    }

    return (
        <StyledVersionValue>
            {isVersionActive(row) ? (
                <StyledVersionActiveIcon
                    className="fas fa-check-circle"
                    aria-hidden="true"
                />
            ) : (
                <StyledVersionInactiveIcon
                    className="fas fa-times-circle"
                    aria-hidden="true"
                />
            )}
            {row.version_name}
        </StyledVersionValue>
    );
}

/**
 * "Grades" cell: lock state badge plus the lock/unlock toggle button.
 * @param {object} row Linked-programme row.
 * @param {object} hook State/handlers from the linked-programmes list hook.
 */
export function GradeCell({ row, hook }) {
    const locked = Number(row?.isGradeLocked) === 1; // 1 = grades locked
    const gradeMeta = getGradeMeta(row);
    const isRowLocking = Boolean(hook.gradeLockLoading?.[row.id]);

    return (
        <StyledGradeCellContent>
            <StyledGradeStatus>
                <StyledGradeBadge $locked={locked}>
                    <i
                        className={locked ? "fal fa-lock" : "fal fa-unlock"}
                        aria-hidden="true"
                    />
                    {locked ? "Grades Locked" : "Grades Unlocked"}
                </StyledGradeBadge>

                {gradeMeta && (
                    <StyledGradeMeta title={gradeMeta}>
                        {gradeMeta}
                    </StyledGradeMeta>
                )}
            </StyledGradeStatus>

            <PermissionsGate scopes={["capgsedit"]} errorProps={{ disabled: true }}>
                <StyledGradeAction
                    type="button"
                    $action={isRowLocking ? "loading" : locked ? "unlock" : "lock"}
                    disabled={
                        hook.isProcessing ||
                        isRowLocking ||
                        hook.gradeLockLoading?.lockAll ||
                        hook.gradeLockLoading?.unlockAll
                    }
                    onClick={() => hook.handleLockGrade(row.id, row.isGradeLocked)}
                    aria-label={locked ? "Unlock grades" : "Lock grades"}
                    title={locked ? "Unlock grades" : "Lock grades"}
                >
                    <i
                        className={
                            isRowLocking
                                ? "fas fa-sync fa-spin"
                                : locked
                                    ? "fas fa-unlock"
                                    : "fas fa-lock"
                        }
                        aria-hidden="true"
                    />
                </StyledGradeAction>
            </PermissionsGate>
        </StyledGradeCellContent>
    );
}

/**
 * "Action" cell: open-programme and unlink buttons.
 * @param {object} row Linked-programme row.
 * @param {object} hook State/handlers from the linked-programmes list hook.
 */
export function ActionsCell({ row, hook }) {
    return (
        <StyledActionGroup>
            <PermissionsGate
                scopes={["matpedit"]}
                RenderError={() => (
                    <IconButton
                        icon="folder-open"
                        variant="solid"
                        disabled
                        ariaLabel="Open programme"
                    />
                )}
            >
                <StyledIconLink
                    to={`/courseAdministration/Programmes/programme/open/${row.id}/details`}
                    title="Open programme"
                >
                    <IconButton
                        icon="folder-open"
                        variant="solid"
                        ariaLabel="Open programme"
                    />
                </StyledIconLink>
            </PermissionsGate>

            <PermissionsGate
                scopes={["matpadd"]}
                RenderError={() => (
                    <IconButton
                        icon="unlink"
                        variant="destructive"
                        disabled
                        ariaLabel="Unlink programme"
                    />
                )}
            >
                <IconButton
                    icon="unlink"
                    variant="destructive"
                    disabled={
                        hook.isProcessing ||
                        Boolean(hook.gradeLockLoading?.[row.id])
                    }
                    onClick={() => hook.startConfirmation("unlink", [row.id], row)}
                    ariaLabel="Unlink programme"
                />
            </PermissionsGate>
        </StyledActionGroup>
    );
}

/**
 * "Module readiness" cell: ready/error summary plus the "Modules" popover toggle.
 * @param {object} row Linked-programme row.
 * @param {object} hook State/handlers from the linked-programmes list hook.
 * @param {object} modulesRow Row the readiness popover is currently open for, if any.
 * @param {Function} onToggleModules Opens/closes the popover for this row (event) => void.
 */
export function ReadinessCell({ row, hook, modulesRow, onToggleModules }) {
    const readiness = getReadiness(row);
    const isOpenForRow = modulesRow?.id === row.id;

    return (
        <StyledReadinessRow>
            <StyledReadinessState>
                <StyledReadinessStateIcon
                    $ready={readiness.problems.length === 0}
                    className={`fas ${
                        readiness.problems.length === 0
                            ? "fa-circle-check"
                            : "fa-times-circle"
                    }`}
                    aria-hidden="true"
                />

                <StyledReadinessText>
                    <StyledReadinessCount>
                        {readiness.readyCount} / {readiness.total} modules ready
                    </StyledReadinessCount>

                    {readiness.warnings.length > 0 && (
                        <StyledReadinessWarnings>
                            {readiness.warnings.map((warning) => (
                                <StyledReadinessWarning key={warning}>
                                    <i
                                        className="fas fa-exclamation-triangle"
                                        aria-hidden="true"
                                    />
                                    {warning}
                                </StyledReadinessWarning>
                            ))}
                        </StyledReadinessWarnings>
                    )}
                </StyledReadinessText>
            </StyledReadinessState>

            <StyledModulesButton
                type="button"
                $active={isOpenForRow}
                disabled={hook.isProcessing}
                aria-expanded={isOpenForRow}
                onClick={(event) => onToggleModules(row, event)}
            >
                Modules
                <i className="fas fa-bars" aria-hidden="true" />
            </StyledModulesButton>
        </StyledReadinessRow>
    );
}

/**
 * Loading-state skeleton row matching either view's column set.
 * @param {boolean} QAview Whether the QA (vs Standard) column set is shown.
 * @param {boolean} isVersionEnabled Whether the Version column is shown.
 */
export function SkeletonRow({ QAview, isVersionEnabled }) {
    return (
        <>
            <StyledCheckboxCell>
                <Skeleton width={16} height={16} />
            </StyledCheckboxCell>

            {QAview ? (
                <>
                    <StyledQaProgrammeCell>
                        <Skeleton width="70%" />
                    </StyledQaProgrammeCell>

                    {isVersionEnabled && (
                        <StyledQaVersionCell>
                            <Skeleton />
                        </StyledQaVersionCell>
                    )}

                    <StyledQaIntakeCell>
                        <Skeleton />
                    </StyledQaIntakeCell>

                    <StyledReadinessCell>
                        <Skeleton />
                    </StyledReadinessCell>

                    <StyledQaGradesCell>
                        <Skeleton />
                    </StyledQaGradesCell>

                    <StyledQaStudentsCell>
                        <Skeleton />
                    </StyledQaStudentsCell>

                    <StyledQaActionsCell>
                        <Skeleton />
                    </StyledQaActionsCell>
                </>
            ) : (
                <>
                    <StyledProgrammeCell>
                        <Skeleton width="70%" />
                    </StyledProgrammeCell>

                    {isVersionEnabled && (
                        <StyledCell>
                            <Skeleton />
                        </StyledCell>
                    )}

                    <StyledCell>
                        <Skeleton />
                    </StyledCell>

                    <StyledCell>
                        <Skeleton />
                    </StyledCell>

                    <StyledCell>
                        <Skeleton />
                    </StyledCell>

                    <StyledCell>
                        <Skeleton />
                    </StyledCell>

                    <StyledStudentCell>
                        <Skeleton />
                    </StyledStudentCell>

                    <StyledCell>
                        <Skeleton />
                    </StyledCell>
                </>
            )}
        </>
    );
}
